import _ from "lodash"

import { ReactNode } from "react"
import { ThemeOptions } from "@mui/material"
import { getIcon } from "utils/icon"
import { IAppConfig } from "services/venue"
import { getDeviceConfigKey } from "providers/venue/utils/prepareConfig"
import {
  navigations as defaultMobileSideMenus,
  bottomNavigations as defaultMobileMainMenus,
  kioskNavigations as defaultKioskSideMenus,
  kioskMainNavigations as defaultKioskMainMenus,
} from "standard"

export interface IMenuConfig {
  to: string
  label: string // Translation key
  order?: number
  icon: ReactNode | null
  icon_comp?: string // Icon Component Name
  icon_path?: string // Svg Path
  image?: string // image url
  // Active Icon
  active_icon?: ReactNode | null
  active_icon_comp?: string // Icon Component Name
  active_icon_path?: string // Svg Path
  active_image?: string // image url
  hidden?: boolean
  qs?: string
  disabled?: boolean
}

// The menu configuration utilized in the app's service configuration.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface GroupMenuConfigs {
  navigations: IMenuConfig[]
  homeNavigations: IMenuConfig[]
  kioskNavigations: IMenuConfig[]
  bottomNavigations: IMenuConfig[]
}

type GenerateMenuConfig = (
  sourceMenus: IMenuConfig[],
  newMenus: IMenuConfig[]
) => IMenuConfig[]

type PrepareMenu = (menuConfig: IMenuConfig) => IMenuConfig
type BindProperty = (menuConfig: IMenuConfig) => void
type IsMenuHidden = (menuConfig: IMenuConfig) => boolean
type GroupMenuByPath = (
  menuConfig: IMenuConfig[],
  field?: string
) => { [k: string]: IMenuConfig }

interface RouteConfig {
  path: string
  element: ReactNode
}

type MergeRoutes = (
  sourceRoutes: RouteConfig[],
  extensionRoutes: RouteConfig[]
) => RouteConfig[]

interface ExtensionConfig {
  themeOptionFactory: (theme: ThemeOptions) => ThemeOptions
  ignoreAppThemeFactory?: boolean
  routes: RouteConfig[]
}

type GetAllExtensionRoutes = (
  extensionList: string[],
  extensionConfigs: ExtensionConfig[] | object
) => RouteConfig[]

export const getAllExtensionRoutes: GetAllExtensionRoutes = (
  extensionList = [],
  extensionConfigs = {},
  deviceConfig = "mobile"
) => {
  if (_.isEmpty(extensionList)) return []

  return _(extensionList)
    .map((extensionName) => {
      // Retrieve rotue config by extension name and the device mode. (kiosk-v, kiosk-h, mobile)
      const routes = _.get(extensionConfigs, [
        extensionName,
        "routes",
        deviceConfig,
      ])
      if (_.isNil(routes) || _.isEmpty(routes)) return null
      return routes
    })
    .compact()
    .flatten()
    .value()
}

export const mergeRoutes: MergeRoutes = (
  sourceRoutes = [],
  extensionRoutes = []
) => {
  const mergedRouteConfigs = _.merge(
    _.keyBy(sourceRoutes, "path"),
    _.keyBy(extensionRoutes, "path")
  )
  return _.values(mergedRouteConfigs)
}

export const groupMenuByPath: GroupMenuByPath = (config, field = "to") => {
  return _.keyBy(config, field)
}

export const isMenuHidden: IsMenuHidden = (config) => !config?.hidden

export const bindIconMenu: BindProperty = (config) => {
  config.icon = getIcon(config)
  config.active_icon = getIcon({
    icon_comp: config?.active_icon_comp,
    icon_path: config?.active_icon_path,
    image: config?.active_image,
  })
}

export const prepareMenu: PrepareMenu = (config) => {
  bindIconMenu(config)
  return { ...config }
}

export const generateMenuConfig: GenerateMenuConfig = (
  sourceMenus = [],
  newMenus = []
) => {
  const groupedSourceMenus = groupMenuByPath(sourceMenus)
  const groupedNewMenus = groupMenuByPath(newMenus)

  // Merge sourceConfigs + newConfigs and then sorting menu by order field.
  return _(groupedSourceMenus)
    .merge(groupedNewMenus)
    .values()
    .filter(isMenuHidden)
    .map(prepareMenu)
    .sortBy("order")
    .value()
}

export const prepareAppNavigationConfig = (
  appConfig: IAppConfig,
  deviceType: string
) => {
  if (!appConfig) return
  const deviceConfigKey = getDeviceConfigKey(appConfig, deviceType)
  const isKiosk = `${deviceConfigKey}`.startsWith("kiosk")

  const defaultMainMenus = isKiosk
    ? defaultKioskMainMenus
    : defaultMobileMainMenus
  const defaultSideMenus = isKiosk
    ? defaultKioskSideMenus
    : defaultMobileSideMenus
  const { main: mainMenusConfig, menus: sideMenusConfig } = _.get(
    appConfig,
    `${deviceConfigKey}.navigation`,
    { main: [], menus: [] }
  )

  const mainMenus = generateMenuConfig(defaultMainMenus, mainMenusConfig)
  const sideMenus = generateMenuConfig(defaultSideMenus, sideMenusConfig)
  return {
    mainMenus,
    sideMenus,
  }
}
